import api, { GetApiReturn, GetApiReturnError, ValidateAxiosError, } from '@/modules/api.service';
import { Endpoints } from '@/shared/constants/endpoints';
import { FindHistoricoDto } from '@/api.types.extra';
import { HistoricoDto } from '@/api.types';

export async function GetHistoricoTabela(tabela: string, idregistro: number | string) {
  return api
    .get(Endpoints.HISTORICO, {
      params: {
        tabela: tabela,
        registro: idregistro,
        data_inicial: '2000-01-01',
        data_final: '3000-01-01',
      },
    })
    .then((response) => {
      return GetApiReturn<HistoricoDto>(response.data as Array<HistoricoDto>);
    })
    .catch((error) => {
      ValidateAxiosError<HistoricoDto>(error, 'Falha ao buscar histórico.');
      return GetApiReturnError<HistoricoDto>();
    });
}

export async function GetHistoricoUsuario(filters: FindHistoricoDto & { incluir_auditoria?: boolean; ocultar_logins?: boolean }) {
  if (filters) {
    filters.skip ??= 0;
    filters.take ??= 10;
  }

  try {
    const response = await api.get<[HistoricoDto[], number]>(Endpoints.HISTORICO, { params: filters });
    return response.data;
  } catch (error) {
    throw ValidateAxiosError(error, 'Falha ao buscar históricos.');
  }
}

/**
   * Mapeamento de chaves para índices.
   */
export const campoMap: Record<string, number> = {
  cpf: 0,
  email: 1,
  data_nascimento: 2,
  data_admissao: 3,
  data_exoneracao: 4,
  data_referencia: 5,
  nome: 6,
  titulo: 7,
  ativo: 8,
  anotacao: 9,
  estadocivil: 10,
  telefone_id: 11,
  celular_id: 12,
  data_entrada: 13,
  data_saida: 14,
  predio_id: 15,
  comarca_id: 16,
  substituta1_id: 17,
  substituta2_id: 18,
  ordem: 19,
  vara_id: 20,
  setor_id: 21,
  id: 22,
  criadoem: 23,
  criadopor: 24,
  editadoem: 25,
  editadopor: 26,
  endereco_id: 27,
  maximo_estagiarios: 28,
  maximo_integrantes: 29,
  agrupar: 30,
  prefixo: 31,
  defensoria_id: 32,
  telefones_id: 33,
  atuacao: 34,
  cnpj: 35,
  razao_social: 36,
  representante_nome: 37,
  representante_cargo: 38,
  bairro: 39,
  cep: 40,
  cidade: 41,
  complemento: 42,
  logradouro: 43,
  numero: 44,
  ddd: 45,
  sigla: 46,
  capacidade: 47,
  status: 48,
  horario_entrada: 49,
  horario_saida: 50,
  anotacao1: 51,
  anotacao2: 52,
  assessor_id: 53,
  ativa: 54,
  ativorelatorio: 55,
  atuacao_id: 56,
  categoria: 57,
  civel: 58,
  codigo: 59,
  colaborador_id: 60,
  corfundotabela: 61,
  corletratabela: 62,
  criminal: 63,
  curso_id: 64,
  data_aquisitiva: 65,
  data_colacao_grau: 66,
  data_fim_estagio: 67,
  data_inicio_estagio: 68,
  data_final: 69,
  data_inicial: 70,
  data_recebimento: 71,
  datarecuperacao: 72,
  defensor_id: 73,
  descricao: 74,
  desligamento: 75,
  diaslivres: 76,
  diretor: 77,
  diretoria_id: 78,
  distrito_id: 79,
  email_institucional: 80,
  email_pessoal: 81,
  entrada: 82,
  estagiario_id: 83,
  estagio_id: 84,
  exibiranotacao: 85,
  exibirrelatorio: 86,
  fim: 87,
  inicio: 88,
  fim_semana: 89,
  foto: 90,
  funcao: 91,
  funcao_id: 91,
  geral: 92,
  instituicao_ensino_id: 93,
  interno: 94,
  login_senha_habilitado: 95,
  login_token_habilitado: 96,
  matricula_defensoria: 97,
  maximo_membros: 98,
  motivo: 99,
  nucleo_id: 101,
  numero_lancamento: 102,
  numero_oab: 103,
  observacao: 104,
  pai_id: 105,
  papel_id: 106,
  pessoa_id: 107,
  podeserresponsavel: 108,
  pontuar: 109,
  portaria: 110,
  rascunho: 111,
  recebenotificacaoes: 112,
  recebenotificacoes: 112,
  referencia: 113,
  relatorios_atividades_habilitado: 114,
  relatorios_atividades_habilitado_em: 115,
  repete_anualmente: 116,
  resolucao: 117,
  responsavel: 118,
  responsavel_id: 118,
  saida: 119,
  sobrenome: 120,
  tipo: 121,
  tipo_estagio: 122,
  ultimo_login: 123,
  username: 124,
  usuario_id: 124,
  visao: 125,
};

/**
 * Transforma um objeto `diff` de chaves string para um mapeamento numérico.
 * @param diff Objeto com as diferenças registradas.
 * @returns Objeto com chaves numéricas.
 */
export function TratarDiferenca(diff: Record<string, any>): Record<number, any> {
  const diffMap: Record<number, any> = {};

  if (diff) {
    try {
      delete diff.editadoem;

      /**
       * Mapeia os campos automaticamente.
       */
      for (const [campo, valor] of Object.entries(diff)) {
        const indice = campoMap[campo];
        if (indice !== undefined) diffMap[indice] = valor;
      }

      /**
       * Se não houver alterações, define um valor nulo.
       */
      if (Object.keys(diffMap).length === 0) diffMap[100] = null; // Histórico vazio
    } catch (error) {
      console.error('Erro ao processar diff:', error);
    }
  }

  return diffMap;
}
